import React, { createContext, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useOverlayDepth } from 'modules/Common/utils/useOverlayDepth';
import { OverlayHostProvider } from 'modules/Common/utils/overlayHost';

// Exposes the Dialog.Content element to descendants so popovers/dropdowns can
// portal *inside* the dialog. Radix Dialog wraps its content in react-remove-scroll,
// which blocks wheel events on anything portaled to <body>. Rendering popovers
// inside the dialog content node keeps wheel scrolling working.
//
// Kept alongside the shared OverlayHostProvider below for the consumers that
// deliberately want "a dialog specifically" rather than "whatever hosts me" —
// see the note in overlayHost.js.
const CgDialogContentContext = createContext(null);

export const useCgDialogPortalContainer = () => useContext(CgDialogContentContext);

// Radix-based modal dialog. Use when a v2 Form lives inside — Radix
// handles focus trap, ESC, scroll lock, and outside-click without us
// hand-wiring it. The visual language (rounded card, soft shadow,
// header/body padding) mirrors StockSupplierModal so v2 forms look
// the same wherever they render.
//
// Widths inherited from the react-bootstrap modals these replaced: `size="lg"`
// was 800px and `size="xl"` 1140px, and 500px was the default. Thirty call
// sites repeated those numbers with nothing saying where they came from. They
// are named here so the vocabulary is ours and the values are changeable in
// one place; `width` still takes a number for genuinely bespoke dialogs.
export const DIALOG_SIZE = {
  md: 500,
  lg: 800,
  xl: 1140,
};

// Composable: pass `title` for the default header, or render your own
// children at the top of the body if you want a custom header.
export default function CgDialog({
  open,
  onOpenChange,
  title,
  description,
  // Controls that belong in the header row beside the title (a filter, a
  // status pill) — same name and placement as CgDrawer's `titleAccessory`.
  // Not part of `title`: that renders an <h2>, and buttons don't go in a heading.
  titleAccessory,
  width,
  size,
  children,
  footer,
  closeOnOverlayClick = true,
  closeButton = true,
  fullScreen = false,
  zIndex,
  // Same name and meaning as CgDrawer's `modal`, and the same reason to turn it
  // off: a modal Radix dialog sets `body { pointer-events: none }` and traps
  // focus, which kills any layer rendered outside it — a react-joyride tour
  // tooltip portals to <body>, so its Next/Skip buttons stop responding and its
  // focus is pulled back into the dialog. `modal={false}` drops both (Radix
  // passes `trapFocus: false, disableOutsidePointerEvents: false`), leaving the
  // dialog to coexist with the tour that explains it. Everything else — the
  // scrim, ESC, the anatomy — is unchanged.
  modal = true,
}) {
  const resolvedWidth = width ?? DIALOG_SIZE[size] ?? 560;
  // Track the content node once it mounts so descendants (e.g. CgFilterDropdown)
  // can portal their popovers inside the dialog and stay within the scroll-lock
  // boundary. useState (not useRef) so the consumer re-renders when it lands.
  const { t } = useTranslation('common');
  const [contentNode, setContentNode] = useState(null);
  const setContentRef = useRef((node) => setContentNode(node)).current;

  // Rung in the open-overlay stack (phase 21). A dialog opened over another
  // dialog gets the next rung automatically, which is what the six hand-written
  // `zIndex={1200}` / `{10010}` call sites were doing by hand. `zIndex` is still
  // honoured for anything that must pin an exact value, but nothing should need
  // it now.
  const { overlay: overlayZ, content: contentZ } = useOverlayDepth(open);
  const resolvedOverlayZ = zIndex ?? overlayZ;
  const resolvedContentZ = zIndex ? zIndex + 1 : contentZ;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      <Dialog.Portal>
        {modal ? (
          <Dialog.Overlay
            style={{ zIndex: resolvedOverlayZ }}
            className="fixed inset-0 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
        ) : (
          // Radix renders no Overlay at all when modal={false} — the overlay IS
          // the thing that blocks the page — so a non-modal dialog would
          // otherwise float over an undimmed app. This scrim only dims and
          // swallows background clicks; it carries no dismiss handler, and
          // anything layered above it (LAYER.tour) stays clickable.
          <div
            aria-hidden="true"
            style={{ zIndex: resolvedOverlayZ }}
            className="fixed inset-0 bg-black/40"
          />
        )}
        <Dialog.Content
          ref={setContentRef}
          onInteractOutside={(e) => {
            if (!closeOnOverlayClick) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (document.querySelector('[data-cg-escape-closes], input[aria-expanded="true"]'))
              e.preventDefault();
          }}
          // Full screen is a SIZE, not a second dialog: same anatomy, same
          // header/body/footer, same portal host — the card simply stops being
          // a card and takes the viewport. Centring is dropped with it, because
          // an inset-0 element has nothing left to centre.
          style={
            fullScreen
              ? // Nearly the viewport, but still a dialog: a margin all round
                // keeps the page visible behind it, which is what tells a user
                // this is a thing they are inside and can leave. Edge-to-edge
                // read as a navigation, not an overlay.
                { inset: '3vh 3vw', width: 'auto', maxWidth: 'none', zIndex: resolvedContentZ }
              : { width: resolvedWidth, maxWidth: '95vw', zIndex: resolvedContentZ }
          }
          className={`fixed focus:outline-none ${
            fullScreen ? '' : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
          }`}
        >
          {/* Visual shell is a child of Dialog.Content (which is the
              react-remove-scroll boundary). Keeping overflow-hidden here for
              rounded corners + scroll containment, while Dialog.Content itself
              stays overflow-visible so descendants portaled to it (e.g. the
              CgFilterDropdown popover) can extend beyond the dialog edges. */}
          <div
            className={`bg-white overflow-hidden flex flex-col rounded-2xl shadow-xl ${
              fullScreen ? 'h-full' : 'max-h-[90vh]'
            }`}
          >
            {title ? (
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
                {/* min-w-0: without it a long description cannot truncate and runs
                    past the dialog's edge (it pushed the Add to quote subtitle out). */}
                <div className="min-w-0 flex-1">
                  <Dialog.Title className="text-base font-semibold text-slate-800 m-0">
                    {title}
                  </Dialog.Title>
                  {description ? (
                    <Dialog.Description className="text-xs text-slate-500 mt-1 m-0">
                      {description}
                    </Dialog.Description>
                  ) : null}
                </div>
                {titleAccessory ? <div className="ms-auto">{titleAccessory}</div> : null}
                {closeButton ? (
                  <Dialog.Close
                    aria-label={t('common:actions.close', 'Close')}
                    className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded -mr-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Dialog.Close>
                ) : null}
              </div>
            ) : null}
            <CgDialogContentContext.Provider value={contentNode}>
              <OverlayHostProvider value={contentNode}>
                <div className="overflow-auto px-5 py-4 flex-1 min-h-0">{children}</div>
                {footer ? (
                  <div
                    className={`border-t border-slate-100 px-5 py-3 bg-slate-50/50 ${
                      fullScreen ? '' : 'rounded-b-2xl'
                    }`}
                  >
                    {footer}
                  </div>
                ) : null}
              </OverlayHostProvider>
            </CgDialogContentContext.Provider>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
